class HindleV2Header extends HTMLElement {
  constructor() {
    super();
    this.drawer = this.querySelector('[data-hindle-v2-drawer]');
    this.overlay = this.querySelector('[data-hindle-v2-drawer-overlay]');
    this.openButtons = this.querySelectorAll('[data-hindle-v2-menu-open]');
    this.closeButtons = this.querySelectorAll('[data-hindle-v2-menu-close]');
    this.accordions = this.querySelectorAll('[data-hindle-v2-accordion-toggle]');

    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.syncHeaderHeight = this.syncHeaderHeight.bind(this);
  }

  connectedCallback() {
    document.documentElement.classList.add('hindle-v2-header-active');
    document.body.classList.add('hindle-v2-header-active');
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.syncHeaderHeight, { passive: true });
    document.addEventListener('keydown', this.onKeydown);
    this.onScroll();
    this.syncHeaderHeight();
    requestAnimationFrame(() => this.syncHeaderHeight());
    window.addEventListener('load', this.syncHeaderHeight, { once: true });

    if (typeof ResizeObserver !== 'undefined') {
      this.headerResizeObserver = new ResizeObserver(() => this.syncHeaderHeight());
      this.headerResizeObserver.observe(this);
    }

    this.openButtons.forEach((btn) => btn.addEventListener('click', () => this.openDrawer()));
    this.closeButtons.forEach((btn) => btn.addEventListener('click', () => this.closeDrawer()));
    this.overlay?.addEventListener('click', () => this.closeDrawer());

    this.accordions.forEach((btn) => {
      btn.addEventListener('click', () => {
        const panel = btn.nextElementSibling;
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        panel?.classList.toggle('is-open', !expanded);
      });
    });

    this.querySelectorAll('.hindle-v2-cart-trigger').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        const cartBubble = document.querySelector('#cart-icon-bubble');
        if (cartBubble && cartBubble !== trigger) {
          event.preventDefault();
          cartBubble.click();
        }
      });
    });
  }

  disconnectedCallback() {
    document.documentElement.classList.remove('hindle-v2-header-active');
    document.body.classList.remove('hindle-v2-header-active');
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.syncHeaderHeight);
    window.removeEventListener('load', this.syncHeaderHeight);
    document.removeEventListener('keydown', this.onKeydown);
    this.headerResizeObserver?.disconnect();
  }

  syncHeaderHeight() {
    const height = Math.ceil(this.getBoundingClientRect().height);
    if (!height || this._lastSyncedHeight === height) return;

    this._lastSyncedHeight = height;
    document.documentElement.style.setProperty('--hindle-v2-header-height', `${height}px`);
    document.documentElement.style.setProperty('--header-height', `${height}px`);
    document.documentElement.style.setProperty('--header-height-live', `${height}px`);
  }

  onScroll() {
    this.classList.toggle('is-scrolled', window.scrollY > 50);
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.closeDrawer();
  }

  openDrawer() {
    this.drawer?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    this.drawer?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}

function syncHindleV2MobileCartBadge() {
  const source = document.getElementById('cart-icon-bubble');
  if (!source) return;

  let count = 0;
  const countNode =
    source.querySelector('.hindle-v2-header__cart-count[aria-hidden="true"]') ||
    source.querySelector('.cart-count-bubble [aria-hidden="true"]');
  if (countNode) count = parseInt(countNode.textContent, 10) || 0;

  document.querySelectorAll('[data-hindle-v2-cart-mobile]').forEach((trigger) => {
    let badge = trigger.querySelector('.hindle-v2-header__cart-count');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'hindle-v2-header__cart-count';
        badge.setAttribute('aria-hidden', 'true');
        trigger.appendChild(badge);
      }
      badge.textContent = count > 999 ? countNode?.textContent || '999+' : String(count);
      if (count > 99) badge.setAttribute('data-wide', 'true');
      else badge.removeAttribute('data-wide');
    } else if (badge) {
      badge.remove();
    }
  });
}

function initHindleV2CartBadgeSync() {
  syncHindleV2MobileCartBadge();

  const source = document.getElementById('cart-icon-bubble');
  if (source) {
    new MutationObserver(() => {
      syncHindleV2MobileCartBadge();
      const badge = source.querySelector('.hindle-v2-header__cart-count');
      if (badge) {
        badge.classList.add('animate--pop');
        setTimeout(() => badge.classList.remove('animate--pop'), 250);
      }
    }).observe(source, {
      childList: true,
      subtree: true,
    });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, syncHindleV2MobileCartBadge);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHindleV2CartBadgeSync);
} else {
  initHindleV2CartBadgeSync();
}

if (!customElements.get('hindle-v2-header')) {
  customElements.define('hindle-v2-header', HindleV2Header);
}

function initHindleV2HeaderSearchCategory() {
  const closePredictiveSearch = (predictiveSearch) => {
    if (!predictiveSearch) return;

    predictiveSearch.classList.add('hindle-v2-search-category-active');

    if (typeof predictiveSearch.close === 'function') {
      predictiveSearch.close(false);
    } else {
      predictiveSearch.removeAttribute('open');
      predictiveSearch.removeAttribute('results');
      predictiveSearch.removeAttribute('loading');
      predictiveSearch.querySelector('input[type="search"]')?.setAttribute('aria-expanded', 'false');
    }
  };

  document.addEventListener(
    'pointerdown',
    (event) => {
      const categorySelect = event.target.closest('select[name="filter.p.product_type"]');
      if (!categorySelect) return;

      const predictiveSearch = categorySelect.closest('predictive-search');
      if (!predictiveSearch) return;

      closePredictiveSearch(predictiveSearch);
    },
    true
  );

  document.addEventListener('change', (event) => {
    if (!event.target.matches('select[name="filter.p.product_type"]')) return;
    event.target.closest('predictive-search')?.classList.remove(
      'hindle-v2-search-category-active'
    );
  });

  document.addEventListener('blur', (event) => {
    if (!event.target.matches('select[name="filter.p.product_type"]')) return;
    event.target.closest('predictive-search')?.classList.remove(
      'hindle-v2-search-category-active'
    );
  }, true);
}

function initHindleV2CollectionFilterDropdowns() {
  document.querySelectorAll('.hindle-v2-collection-filter').forEach((bar) => {
    const dropdowns = bar.querySelectorAll('.hindle-v2-collection-filter__dropdown');
    dropdowns.forEach((dropdown) => {
      if (dropdown.dataset.hindleV2DropdownInit) return;
      dropdown.dataset.hindleV2DropdownInit = 'true';

      dropdown.addEventListener('toggle', () => {
        if (!dropdown.open) return;
        dropdowns.forEach((other) => {
          if (other !== dropdown) other.open = false;
        });
      });
    });
  });

  if (document.documentElement.dataset.hindleV2FilterDropdownClickOutside) return;
  document.documentElement.dataset.hindleV2FilterDropdownClickOutside = 'true';

  document.addEventListener('click', (event) => {
    document.querySelectorAll('.hindle-v2-collection-filter__dropdown[open]').forEach((dropdown) => {
      if (!dropdown.contains(event.target)) {
        dropdown.open = false;
      }
    });
  });
}

function initHindleV2CollectionFilterSheets() {
  document.querySelectorAll('[data-hindle-v2-filter-open]').forEach((openBtn) => {
    const sheetId = openBtn.getAttribute('aria-controls');
    const sheet = sheetId ? document.getElementById(sheetId) : null;
    if (!sheet) return;

    const openSheet = () => {
      sheet.hidden = false;
      sheet.setAttribute('aria-hidden', 'false');
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    };

    const closeSheet = () => {
      sheet.hidden = true;
      sheet.setAttribute('aria-hidden', 'true');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    };

    openBtn.addEventListener('click', openSheet);
    sheet.querySelectorAll('[data-hindle-v2-filter-close]').forEach((el) => {
      el.addEventListener('click', closeSheet);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !sheet.hidden) closeSheet();
    });
  });
}

function trimHindleV2JudgemeBadgeText(root) {
  if (typeof window.__hindleV2TrimJudgemeText === 'function') {
    window.__hindleV2TrimJudgemeText(root);
  }
}

function hindleV2ProductReviewsHasContent(reviews) {
  if (!reviews) return false;

  const badges = reviews.querySelectorAll('.jdgm-prev-badge, .jdgm-preview-badge, .jdgm-widget');
  if (!badges.length) return false;

  for (const badge of badges) {
    const style = window.getComputedStyle(badge);
    if (style.display === 'none' || style.visibility === 'hidden') continue;

    const rating = badge.getAttribute('data-average-rating');
    if (rating === '0.00' || rating === '0') continue;

    const stars = badge.querySelector('.jdgm-prev-badge__stars, .jdgm-stars');
    if (stars && stars.querySelector('.jdgm-star.jdgm--on, .jdgm-star.jdgm--half')) {
      return true;
    }

    const textEl = badge.querySelector('.jdgm-prev-badge__text, .jdgm-preview-badge__text, a.jdgm-prev-badge__text');
    const text = textEl
      ? textEl.textContent.replace(/\s+/g, ' ').trim().replace(/^\(+|\)+$/g, '').trim()
      : badge.textContent.replace(/\s+/g, ' ').trim();

    if (text && !/^0 reviews?$/i.test(text) && !/^no reviews?$/i.test(text)) {
      return true;
    }
  }

  return false;
}

function updateHindleV2ProductReviewsVisibility(root) {
  const scope = root && root.querySelector ? root : document;
  scope.querySelectorAll('.hindle-v2-product-info__reviews').forEach((reviews) => {
    reviews.classList.toggle('is-empty', !hindleV2ProductReviewsHasContent(reviews));
  });
}

function hindleV2JudgemeCarouselCards(widget) {
  if (!widget) return [];
  return Array.from(
    widget.querySelectorAll('.jdgm-cards-wrapper .jdgm-media-card, .jdgm-testimonials-container .jdgm-carousel-item')
  );
}

function hindleV2JudgemeNativeArrows(widget) {
  if (!widget) return { prev: null, next: null };
  return {
    prev: widget.querySelector('.jdgm-arrows--bottom .jdgm-arrow:first-child, .jdgm-content > .jdgm-arrow:first-child'),
    next: widget.querySelector('.jdgm-arrows--bottom .jdgm-arrow:last-child, .jdgm-content > .jdgm-arrow:last-child'),
  };
}

function hideHindleV2JudgemeNativeArrows(widget) {
  if (!widget) return;
  widget.querySelectorAll('.jdgm-arrows--bottom, .jdgm-content > .jdgm-arrow').forEach((node) => {
    node.style.setProperty('display', 'none', 'important');
  });
}

function hindleV2JudgemeSlideTrack(widget) {
  if (!widget) return null;
  return (
    widget.querySelector('.jdgm-videos-container') ||
    widget.querySelector('.jdgm-testimonials-container') ||
    widget.querySelector('.jdgm-cards-wrapper')
  );
}

function enableHindleV2JudgemeSlideEffect(widget) {
  const track = hindleV2JudgemeSlideTrack(widget);
  if (!track || track.dataset.hindleV2JdgmSlide === 'true') return;
  track.dataset.hindleV2JdgmSlide = 'true';
  track.style.setProperty(
    'transition',
    'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1), margin 0.45s cubic-bezier(0.4, 0, 0.2, 1), left 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
    'important'
  );
}

function prepareHindleV2JudgemeWidget(widget) {
  if (!widget || widget.dataset.hindleV2JdgmPrepared === 'true') return;
  widget.dataset.hindleV2JdgmPrepared = 'true';
  widget.classList.remove('jdgm-hidden');
  hideHindleV2JudgemeNativeArrows(widget);
  enableHindleV2JudgemeSlideEffect(widget);
}

function syncHindleV2JudgemeCarouselNav(shell) {
  if (!shell) return;

  const widget = shell.querySelector('.jdgm-widget');
  const prevBtn = shell.querySelector('.hindle-v2-product-reviews__nav--prev');
  const nextBtn = shell.querySelector('.hindle-v2-product-reviews__nav--next');
  if (!widget || !prevBtn || !nextBtn) return;

  prepareHindleV2JudgemeWidget(widget);
  enableHindleV2JudgemeSlideEffect(widget);

  const cards = hindleV2JudgemeCarouselCards(widget);
  const { prev, next } = hindleV2JudgemeNativeArrows(widget);
  const cardCount = cards.length;
  const needsNav = cardCount > 1;

  shell.classList.toggle('has-nav', needsNav);
  prevBtn.disabled = !needsNav || Boolean(prev?.disabled);
  nextBtn.disabled = !needsNav || Boolean(next?.disabled);
}

function initHindleV2JudgemeCarouselNav(root) {
  const scope = root && root.querySelector ? root : document;

  scope.querySelectorAll('[data-hindle-v2-jdgm-carousel]').forEach((shell) => {
    const widget = shell.querySelector('.hindle-v2-product-reviews__block .jdgm-widget');
    if (!widget) return;

    const blockId = widget.getAttribute('data-jdgm-block-id');
    if (!blockId) return;

    if (shell.dataset.hindleV2JdgmNavInit !== 'true') {
      shell.dataset.hindleV2JdgmNavInit = 'true';

      const prevBtn = shell.querySelector('.hindle-v2-product-reviews__nav--prev');
      const nextBtn = shell.querySelector('.hindle-v2-product-reviews__nav--next');

      prevBtn?.addEventListener('click', () => {
        if (typeof window.jdgmPreviousCard === 'function') {
          window.jdgmPreviousCard(blockId);
        }
        window.setTimeout(() => syncHindleV2JudgemeCarouselNav(shell), 60);
        window.setTimeout(() => syncHindleV2JudgemeCarouselNav(shell), 300);
      });

      nextBtn?.addEventListener('click', () => {
        if (typeof window.jdgmNextCard === 'function') {
          window.jdgmNextCard(blockId);
        }
        window.setTimeout(() => syncHindleV2JudgemeCarouselNav(shell), 60);
        window.setTimeout(() => syncHindleV2JudgemeCarouselNav(shell), 300);
      });
    }

    syncHindleV2JudgemeCarouselNav(shell);
  });
}

function initHindleV2JudgemeCarouselNavGuard(root) {
  initHindleV2JudgemeCarouselNav(root);

  const scope = root && root.querySelector ? root : document;
  scope.querySelectorAll('[data-hindle-v2-jdgm-carousel]').forEach((shell) => {
    if (shell.dataset.hindleV2JdgmNavGuard === 'true') return;
    shell.dataset.hindleV2JdgmNavGuard = 'true';

    const widget = shell.querySelector('.hindle-v2-product-reviews__block .jdgm-widget');
    if (!widget) return;

    let syncFrame;
    new MutationObserver(() => {
      window.cancelAnimationFrame(syncFrame);
      syncFrame = window.requestAnimationFrame(() => syncHindleV2JudgemeCarouselNav(shell));
    }).observe(widget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'disabled'],
    });
  });
}

function hideHindleV2AddonsJudgeme(root) {
  const scope = root && root.querySelector ? root : document;
  scope.querySelectorAll('.hindle-v2-product-info__addons [class*="jdgm-"]').forEach((node) => {
    node.remove();
  });
}

function initHindleV2AddonsJudgemeGuard(root) {
  hideHindleV2AddonsJudgeme(root);

  const scope = root && root.querySelector ? root : document;
  scope.querySelectorAll('.hindle-v2-product-info__addons').forEach((addons) => {
    if (addons.dataset.hindleV2AddonsJudgemeGuard === 'true') return;
    addons.dataset.hindleV2AddonsJudgemeGuard = 'true';

    new MutationObserver(() => {
      hideHindleV2AddonsJudgeme(addons);
    }).observe(addons, {
      childList: true,
      subtree: true,
    });
  });
}

function initHindleV2JudgemeBadgeText() {
  hideHindleV2AddonsJudgeme();
  initHindleV2AddonsJudgemeGuard();
  trimHindleV2JudgemeBadgeText();
  updateHindleV2ProductReviewsVisibility();
  initHindleV2JudgemeCarouselNav();
  initHindleV2JudgemeCarouselNavGuard();

  const scheduleTrim = (root) => {
    hideHindleV2AddonsJudgeme(root);
    initHindleV2AddonsJudgemeGuard(root);
    trimHindleV2JudgemeBadgeText(root);
    updateHindleV2ProductReviewsVisibility(root);
    initHindleV2JudgemeCarouselNav(root);
    initHindleV2JudgemeCarouselNavGuard(root);
    window.requestAnimationFrame(() => {
      hideHindleV2AddonsJudgeme(root);
      initHindleV2AddonsJudgemeGuard(root);
      trimHindleV2JudgemeBadgeText(root);
      updateHindleV2ProductReviewsVisibility(root);
      initHindleV2JudgemeCarouselNav(root);
    });
  };

  if (window.jdgm && typeof window.jdgm.docReady === 'function') {
    window.jdgm.docReady(() => {
      scheduleTrim();
      window.setTimeout(() => scheduleTrim(), 100);
      window.setTimeout(() => scheduleTrim(), 500);
      window.setTimeout(() => scheduleTrim(), 1500);
    });
  } else {
    window.setTimeout(() => scheduleTrim(), 500);
    window.setTimeout(() => scheduleTrim(), 1500);
  }

  document.querySelectorAll('.hindle-v2-product-info__reviews').forEach((reviews) => {
    if (reviews.dataset.hindleV2JudgemeObserved === 'true') return;
    reviews.dataset.hindleV2JudgemeObserved = 'true';

    let trimFrame;
    new MutationObserver(() => {
      window.cancelAnimationFrame(trimFrame);
      trimFrame = window.requestAnimationFrame(() => {
        trimHindleV2JudgemeBadgeText(reviews);
        updateHindleV2ProductReviewsVisibility(reviews);
      });
    }).observe(reviews, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'data-average-rating'],
    });
  });
}

function initHindleV2RelatedRecommendations(root) {
  const scope = root && root.querySelector ? root : document;

  scope.querySelectorAll('[data-hindle-v2-related-url]').forEach((container) => {
    if (container.dataset.hindleV2RelatedInit === 'true') return;

    const url = container.dataset.hindleV2RelatedUrl;
    if (!url) return;

    container.dataset.hindleV2RelatedInit = 'true';

    const loadRecommendations = () => {
      if (container.dataset.hindleV2RelatedLoaded === 'true') return;
      container.dataset.hindleV2RelatedLoaded = 'true';

      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text.replace(/<template\b[^>]*>|<\/template>/g, '');
          const recommendations = html.querySelector('[data-hindle-v2-related-url]');
          if (!recommendations || !recommendations.innerHTML.trim()) return;
          if (!recommendations.querySelector('.hindle-v2-product-card-wrap')) return;
          container.innerHTML = recommendations.innerHTML;
        })
        .catch(() => {});
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        loadRecommendations();
      }, { rootMargin: '0px 0px 400px 0px' });
      observer.observe(container);
    } else {
      loadRecommendations();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initHindleV2HeaderSearchCategory();
    initHindleV2CollectionFilterDropdowns();
    initHindleV2CollectionFilterSheets();
    initHindleV2JudgemeBadgeText();
    initHindleV2RelatedRecommendations();
  });
} else {
  initHindleV2HeaderSearchCategory();
  initHindleV2CollectionFilterDropdowns();
  initHindleV2CollectionFilterSheets();
  initHindleV2JudgemeBadgeText();
  initHindleV2RelatedRecommendations();
}

document.addEventListener('shopify:section:load', (event) => {
  hideHindleV2AddonsJudgeme(event.target);
  initHindleV2AddonsJudgemeGuard(event.target);
  trimHindleV2JudgemeBadgeText(event.target);
  updateHindleV2ProductReviewsVisibility(event.target);
  initHindleV2JudgemeCarouselNav(event.target);
  initHindleV2JudgemeCarouselNavGuard(event.target);
  initHindleV2RelatedRecommendations(event.target);
});
